import { useCallback, useEffect, useRef, useState } from "react";
import {
  getRunnerLanguages,
  PlaygroundRunError,
  runOnServer,
  sendLiveInput,
  startLiveRun,
  stopLiveRun,
  streamLiveRun,
  type PlaygroundRunResult,
} from "../../api/codePlayground";
import { appendChunk, applyEvent, driveLiveRun, exitNote, type ConsoleChunk, type LiveTransport } from "../../vendor/nga-liveconsole";
import {
  buildCssDocument,
  buildPageDocument,
  buildReactDocument,
  buildScriptDocument,
  listenToRun,
  looksLikeHtml,
  newRunId,
  phpRequestInput,
  PHP_ROUTER,
  PHP_ROUTER_PATH,
  resolvePath,
  type ConsoleEntry,
  type NavRequest,
} from "./browserRun";
import type { PlaygroundLanguage } from "./languages";
import { htmlFiles, startPage, type PlayProject } from "./project";

/** Running code, wherever it runs: one state shape for the browser frame and the server. */

export type RunPhase = "idle" | "running" | "done";

export interface RunMeta {
  /** ok | compile-error | runtime-error | time-limit | memory-limit | output-limit | internal-error | refused */
  verdict: string;
  where: "browser" | "server";
  engine?: string | null;
  time_ms?: number | null;
  memory_kb?: number | null;
  exit_code?: number | null;
}

// A live run's console and session handling are nga-liveconsole's (shared with Task Mentor's grading).
export type { ConsoleChunk } from "../../vendor/nga-liveconsole";

export interface RunState {
  phase: RunPhase;
  entries: ConsoleEntry[];
  meta: RunMeta | null;
  /** The program's own output (stdout / console.log), for "Check". */
  output: string;
  /** srcdoc of the preview frame (web pages, React, browser JS, a PHP page). */
  doc: string | null;
  runId: string | null;
  /** The project path of the page in the preview (web projects). */
  page: string | null;
  /** Pages visited before it (Back in the preview). */
  history: string[];
  /** A live run (the program runs while the student watches and types): its transcript. */
  console: ConsoleChunk[] | null;
  /** The live run in progress: its session and stage. */
  live: { id: string | null; stage: "build" | "run" | null } | null;
  /** When the program last printed something (live runs: "is it waiting for me?"). */
  lastOutputAt: number;
  /** Arduino: the compiled firmware (Intel HEX) the simulator runs, and how much of the chip it uses. */
  firmware: Firmware | null;
}

export interface Firmware {
  hex: string;
  /** Bytes of flash / RAM the sketch uses, and the chip's (Uno: 32 256 usable flash, 2 048 RAM). */
  flash: number | null;
  flashMax: number | null;
  ram: number | null;
  ramMax: number | null;
}

/** arduino-cli's size lines → numbers (null when it didn't say). */
export function firmwareSize(log: string): Pick<Firmware, "flash" | "flashMax" | "ram" | "ramMax"> {
  const f = /Sketch uses (\d+) bytes .*?Maximum is (\d+) bytes/.exec(log);
  const r = /Global variables use (\d+) bytes .*?Maximum is (\d+) bytes/.exec(log);
  return { flash: f ? +f[1] : null, flashMax: f ? +f[2] : null, ram: r ? +r[1] : null, ramMax: r ? +r[2] : null };
}

/** The compiler's other messages (warnings), without the size lines. */
const compileNotes = (log: string) =>
  log
    .split("\n")
    .filter((l) => !/^(Sketch uses|Global variables use)/.test(l))
    .join("\n")
    .trim();

const IDLE: RunState = { phase: "idle", entries: [], meta: null, output: "", doc: null, runId: null, page: null, history: [], console: null, live: null, lastOutputAt: 0, firmware: null };

/** The MIS relay as nga-liveconsole's transport (POST/GET/DELETE /code-playground/sessions…). */
type LiveRequest = Parameters<typeof startLiveRun>[0];
const relayTransport: LiveTransport<LiveRequest> = { start: startLiveRun, stream: streamLiveRun, input: sendLiveInput, stop: stopLiveRun };
const BROWSER_TIMEOUT_MS = 6000;

/** The runner's languages (null until known), asked again whenever a playground opens. */
export function useRunnerLanguages(): { keys: Set<string> | null; available: boolean | null; interactive: boolean; message?: string } {
  const [state, setState] = useState<{ keys: Set<string> | null; available: boolean | null; interactive: boolean; message?: string }>({ keys: null, available: null, interactive: false });
  useEffect(() => {
    let alive = true;
    getRunnerLanguages().then((r) => {
      if (alive) setState({ keys: new Set(r.languages.map((l) => l.key)), available: r.available, interactive: r.interactive === true, message: r.message });
    });
    return () => {
      alive = false;
    };
  }, []);
  return state;
}

/** Server answer → console lines. */
function serverEntries(r: PlaygroundRunResult, showStdout = true): ConsoleEntry[] {
  const entries: ConsoleEntry[] = [];
  if (r.compile_output && r.verdict === "compile-error") entries.push({ level: "error", text: r.compile_output });
  else if (r.compile_output) entries.push({ level: "warn", text: r.compile_output });
  if (showStdout && r.stdout) entries.push({ level: "log", text: r.stdout.replace(/\n$/, "") });
  if (r.stderr) entries.push({ level: r.verdict === "ok" ? "warn" : "error", text: r.stderr.replace(/\n$/, "") });
  return entries;
}

const serverMeta = (r: PlaygroundRunResult): RunMeta => ({ verdict: r.verdict, where: "server", engine: r.engine, time_ms: r.time_ms, memory_kb: r.memory_kb, exit_code: r.exit_code });

export function useRunner() {
  const [state, setState] = useState<RunState>(IDLE);
  const abort = useRef<AbortController | null>(null);
  const startedAt = useRef(0);
  /** What the last run ran: navigation in its pages uses the same files. */
  const last = useRef<{ lang: PlaygroundLanguage; project: PlayProject } | null>(null);
  /** The live run's session and its event stream. */
  const liveRun = useRef<{ id: string | null; runId: string; stream: AbortController } | null>(null);

  const endLive = useCallback(() => {
    const l = liveRun.current;
    liveRun.current = null;
    if (!l) return;
    l.stream.abort();
    if (l.id) void stopLiveRun(l.id);
  }, []);
  useEffect(() => () => endLive(), [endLive]);

  const finishBrowser = useCallback((runId: string) => {
    setState((s) => {
      if (s.runId !== runId || s.phase !== "running") return s;
      const failed = s.entries.some((e) => e.level === "error");
      return { ...s, phase: "done", meta: { verdict: failed ? "runtime-error" : "ok", where: "browser", time_ms: Math.round(performance.now() - startedAt.current) } };
    });
  }, []);

  /** Shows a page in the preview: an HTML file as is, a .php file run on the code runner. */
  const openPage = useCallback(
    async (path: string, method: "GET" | "POST", query: string, fields: [string, string][], from: string | null) => {
      const ctx = last.current;
      if (!ctx) return;
      const files = ctx.project.files;
      const file = files.find((f) => f.path === path);
      const runId = newRunId();
      const history = (s: RunState) => (from ? [...s.history, from].slice(-30) : s.history);
      if (!file) {
        setState((s) => ({ ...s, entries: [...s.entries, { level: "error", text: `Page not found: ${path}` }] }));
        return;
      }
      if (/\.html?$/i.test(path)) {
        const { doc, missing } = buildPageDocument(file.content, path, files, runId, "");
        startedAt.current = performance.now();
        setState((s) => ({
          ...s,
          phase: "running",
          doc,
          runId,
          page: path,
          history: history(s),
          entries: [...s.entries, ...missing.map((m) => ({ level: "warn" as const, text: `${path}: links to ${m}, which isn't one of the files` }))],
        }));
        return;
      }
      if (/\.php$/i.test(path)) {
        const controller = new AbortController();
        abort.current?.abort();
        abort.current = controller;
        setState((s) => ({ ...s, phase: "running", runId, entries: [...s.entries, { level: "info", text: `${method} ${path}${query ? `?${query}` : ""}${fields.length ? ` (${fields.length} field${fields.length === 1 ? "" : "s"})` : ""}` }] }));
        try {
          const r = await runOnServer(
            { language: "php", code: PHP_ROUTER, entry: PHP_ROUTER_PATH, files, stdin: phpRequestInput(path, method, query, fields) },
            controller.signal,
          );
          const { doc } = buildPageDocument(r.stdout || "<p></p>", path, files, runId, "");
          startedAt.current = performance.now();
          setState((s) =>
            s.runId !== runId ? s : { ...s, phase: r.verdict === "ok" ? "running" : "done", doc, page: path, history: history(s), meta: serverMeta(r), entries: [...s.entries, ...serverEntries(r, false)] },
          );
        } catch (e) {
          const err = e instanceof PlaygroundRunError ? e : new PlaygroundRunError("RUN_FAILED", "The page couldn't be run.");
          if (err.code === "CANCELED") return;
          setState((s) => (s.runId !== runId ? s : { ...s, phase: "done", entries: [...s.entries, { level: "error", text: `${path}: ${err.message}` }], meta: { verdict: "refused", where: "server" } }));
        } finally {
          if (abort.current === controller) abort.current = null;
        }
        return;
      }
      setState((s) => ({ ...s, entries: [...s.entries, { level: "warn", text: `${path} isn't a page (only .html and .php files open in the preview).` }] }));
    },
    [],
  );

  /** A link followed / form sent in the preview. */
  const navigate = useCallback(
    (nav: NavRequest) => {
      const from = state.page ?? last.current?.project.entry ?? "index.html";
      const { path, query } = resolvePath(from, nav.href || from);
      void openPage(path, nav.method, query, nav.fields, from);
    },
    [state.page, openPage],
  );

  const back = useCallback(() => {
    const prev = state.history[state.history.length - 1];
    if (!prev) return;
    setState((s) => ({ ...s, history: s.history.slice(0, -1) }));
    void openPage(prev, "GET", "", [], null);
  }, [state.history, openPage]);

  // A browser run's console: listened to for as long as it is the current run (also after it
  // "finished" — a click on the page can still log). In an effect keyed on the run, so React's
  // remounts (StrictMode, a closed and reopened playground) re-attach it.
  const browserRunId = state.doc ? state.runId : null;
  const navRef = useRef(navigate);
  navRef.current = navigate;
  useEffect(() => {
    if (!browserRunId) return;
    const runId = browserRunId;
    const finish = () => finishBrowser(runId);
    const unlisten = listenToRun(
      runId,
      (entry) =>
        setState((s) =>
          s.runId !== runId
            ? s
            : {
                ...s,
                entries: [...s.entries, entry].slice(-1000),
                output: entry.level === "log" ? (s.output ? `${s.output}\n${entry.text}` : entry.text) : s.output,
              },
        ),
      finish,
      (nav) => navRef.current(nav),
    );
    // Pages keep running (timers, clicks): stop waiting after a while, keep listening.
    const timer = window.setTimeout(finish, BROWSER_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
      unlisten();
    };
  }, [browserRunId, finishBrowser]);

  const stop = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    if (liveRun.current) {
      endLive();
      setState((s) =>
        s.phase === "running" ? { ...s, phase: "done", live: null, console: appendChunk(s.console ?? [], { kind: "note", text: "Stopped." }), meta: s.meta ?? { verdict: "refused", where: "server" } } : s,
      );
      return;
    }
    setState((s) =>
      s.phase === "running"
        ? { ...s, phase: "done", doc: s.doc && !s.meta ? null : s.doc, meta: s.meta ?? { verdict: "refused", where: s.doc ? "browser" : "server" }, entries: [...s.entries, { level: "warn", text: "Stopped." }] }
        : s,
    );
  }, [endLive]);

  const clear = useCallback(
    () =>
      setState((s) =>
        // A live run keeps going: only its transcript is cleared.
        s.live
          ? { ...s, console: [], entries: [] }
          : // The simulated Arduino keeps running: only the log is cleared.
            { ...IDLE, doc: s.doc, runId: s.runId, page: s.page, history: s.history, meta: s.meta && { ...s.meta }, firmware: s.firmware },
      ),
    [],
  );

  const run = useCallback(
    /**
     * `check`: an exercise checks the output. For Arduino that output is what the sketch prints
     * on Serial in its first simulated seconds (with `stdin` typed into the Serial Monitor).
     */
    async (lang: PlaygroundLanguage, project: PlayProject, stdin: string, opts: { check?: string } = {}) => {
      abort.current?.abort();
      abort.current = null;
      endLive();
      last.current = { lang, project };
      const entry = project.files.find((f) => f.path === project.entry);
      const others = project.files.filter((f) => f.path !== project.entry);

      if (lang.browser) {
        const runId = newRunId();
        startedAt.current = performance.now();
        if (lang.browser === "web") {
          const first = startPage(project);
          if (!first && lang.id === "css") {
            const css = project.files.filter((f) => /\.css$/i.test(f.path)).map((f) => f.content).join("\n");
            setState({ ...IDLE, phase: "running", doc: buildCssDocument(css, runId), runId, page: null });
            return;
          }
          if (!first) {
            setState({ ...IDLE, phase: "done", entries: [{ level: "error", text: "Add an .html page (index.html) to show." }], meta: { verdict: "refused", where: "browser" } });
            return;
          }
          setState({ ...IDLE, phase: "running", runId: null });
          await openPage(first, "GET", "", [], null);
          return;
        }
        const doc = lang.browser === "react" ? buildReactDocument(project, runId) : buildScriptDocument(project, runId, stdin);
        setState({ ...IDLE, phase: "running", doc, runId });
        return;
      }

      const runId = newRunId();
      const controller = new AbortController();
      abort.current = controller;
      setState({ ...IDLE, phase: "running", runId });
      try {
        // One file under the language's usual name: let the server name it (Java: after its public class).
        const named = others.length > 0 || project.entry !== lang.file;
        const r = await runOnServer({ language: lang.runner!, code: entry?.content ?? "", entry: named ? project.entry : undefined, files: others, stdin }, controller.signal);
        if (lang.firmware) {
          const log = r.compile_output ?? "";
          const hex = r.verdict === "ok" && r.stdout.trimStart().startsWith(":") ? r.stdout : null;
          const size = firmwareSize(log);
          const entries: ConsoleEntry[] = [];
          if (hex) {
            const pct = (a: number | null, b: number | null) => (a !== null && b ? ` (${Math.round((a / b) * 100)}%)` : "");
            entries.push({
              level: "info",
              text: `Compiled for the Arduino Uno${size.flash !== null ? `: ${size.flash.toLocaleString()} bytes of flash${pct(size.flash, size.flashMax)}, ${size.ram?.toLocaleString() ?? "?"} bytes of RAM${pct(size.ram, size.ramMax)}` : ""}. It runs in the simulator.`,
            });
            const notes = compileNotes(log);
            if (notes) entries.push({ level: "warn", text: notes });
          } else entries.push(...serverEntries({ ...r, stdout: "" }));
          let output = "";
          if (hex && opts.check !== undefined) {
            const { runHeadless } = await import("./arduino/sim");
            const { parseCircuit, DIAGRAM_FILE } = await import("./arduino/parts");
            const circuit = parseCircuit(project.files.find((f) => f.path === DIAGRAM_FILE)?.content);
            output = (await runHeadless(hex, circuit, { ms: 3000, input: stdin || undefined, stopAfterChars: opts.check.length + 400, signal: controller.signal })).serial;
          }
          setState((s) => (s.runId !== runId ? s : { ...s, phase: "done", entries, output, meta: serverMeta(r), firmware: hex ? { hex, ...size } : null }));
          return;
        }
        // PHP that prints a page (and a project with pages): show it, links and forms working.
        const page = lang.id === "php" && r.verdict === "ok" && looksLikeHtml(r.stdout) ? buildPageDocument(r.stdout, project.entry, project.files, runId, "").doc : null;
        startedAt.current = performance.now();
        setState((s) =>
          s.runId !== runId
            ? s
            : { ...s, phase: "done", entries: serverEntries(r), output: r.stdout, meta: serverMeta(r), doc: page, page: page ? project.entry : null },
        );
      } catch (e) {
        const err = e instanceof PlaygroundRunError ? e : new PlaygroundRunError("RUN_FAILED", "The code couldn't be run.");
        if (err.code === "CANCELED") return;
        setState((s) => (s.runId !== runId ? s : { ...s, phase: "done", entries: [{ level: "error", text: err.message }], meta: { verdict: "refused", where: "server" } }));
      } finally {
        if (abort.current === controller) abort.current = null;
      }
    },
    [openPage],
  );

  /**
   * A live run: the program starts on the code runner and its output streams in as it is
   * printed; what the student types (sendLine) reaches its stdin while it waits. Answers typed
   * in advance (the Input panel) are sent as soon as it starts. Falls back to a batch run
   * when the runner can't run programs live.
   */
  const runLive = useCallback(
    async (lang: PlaygroundLanguage, project: PlayProject, prefill: string) => {
      abort.current?.abort();
      abort.current = null;
      endLive();
      last.current = { lang, project };
      const runId = newRunId();
      const stream = new AbortController();
      liveRun.current = { id: null, runId, stream };
      const mine = (s: RunState) => s.runId === runId;
      setState({ ...IDLE, phase: "running", runId, console: [], live: { id: null, stage: null }, lastOutputAt: Date.now() });

      const entry = project.files.find((f) => f.path === project.entry);
      const others = project.files.filter((f) => f.path !== project.entry);
      const named = others.length > 0 || project.entry !== lang.file;
      let id = "";
      await driveLiveRun(relayTransport, { language: lang.runner!, code: entry?.content ?? "", entry: named ? project.entry : undefined, files: others }, {
        signal: stream.signal,
        current: () => liveRun.current?.runId === runId,
        prefill,
        failed: (e) => {
          const err = e instanceof PlaygroundRunError ? e : new PlaygroundRunError("RUN_FAILED", "The program couldn't start.");
          liveRun.current = null;
          if (err.code === "INTERACTIVE_UNAVAILABLE") return void run(lang, project, prefill);
          setState((s) => (mine(s) ? { ...s, phase: "done", live: null, console: [{ kind: "err", text: err.message }], meta: { verdict: "refused", where: "server" } } : s));
        },
        started: (started) => {
          id = started;
          liveRun.current!.id = id;
          setState((s) => (mine(s) ? { ...s, live: { id, stage: s.live?.stage ?? null } } : s));
        },
        typed: (text) => setState((s) => (mine(s) ? { ...s, console: appendChunk(s.console ?? [], { kind: "in", text }) } : s)),
        event: (ev) => {
          if (ev.event !== "exit") return void setState((s) => (mine(s) ? applyEvent(s, ev, id) : s));
          const d = ev.data;
          setState((s) => {
            if (!mine(s)) return s;
            const how = exitNote(d);
            // PHP that printed a page: show it.
            const ctx = last.current;
            const doc = ctx?.lang.id === "php" && d.verdict === "ok" && looksLikeHtml(s.output) ? buildPageDocument(s.output, ctx.project.entry, ctx.project.files, runId, "").doc : null;
            return {
              ...s,
              phase: "done",
              live: null,
              doc,
              page: doc ? ctx!.project.entry : null,
              console: how ? appendChunk(s.console ?? [], { kind: "note", text: how }) : s.console,
              meta: { verdict: d.verdict, where: "server", time_ms: d.time_ms, memory_kb: d.memory_kb, exit_code: d.code },
            };
          });
        },
        lost: (e) => {
          const msg = e instanceof PlaygroundRunError ? e.message : "The connection to the program was lost.";
          setState((s) => (mine(s) ? { ...s, console: appendChunk(s.console ?? [], { kind: "err", text: `${msg}\n` }) } : s));
        },
        ended: (exited) => {
          if (liveRun.current?.runId === runId) liveRun.current = null;
          if (!exited) setState((s) => (mine(s) && s.phase === "running" ? { ...s, phase: "done", live: null, meta: s.meta ?? { verdict: "refused", where: "server" } } : s));
        },
      });
    },
    [endLive, run],
  );

  /** A line the student typed into the live console (sent with its newline). */
  const sendLine = useCallback((text: string) => {
    const l = liveRun.current;
    if (!l?.id) return;
    const data = `${text}\n`;
    setState((s) => (s.runId === l.runId ? { ...s, console: appendChunk(s.console ?? [], { kind: "in", text: data }), lastOutputAt: Date.now() } : s));
    void sendLiveInput(l.id, { data }).catch((e) =>
      setState((s) => (s.runId === l.runId ? { ...s, console: appendChunk(s.console ?? [], { kind: "err", text: `${e?.message ?? "The input couldn't be sent."}\n` }) } : s)),
    );
  }, []);

  /** End of input (Ctrl+D): a program reading until the end stops waiting. */
  const sendEof = useCallback(() => {
    const l = liveRun.current;
    if (!l?.id) return;
    setState((s) => (s.runId === l.runId ? { ...s, console: appendChunk(s.console ?? [], { kind: "note", text: "End of input sent." }) } : s));
    void sendLiveInput(l.id, { eof: true }).catch(() => undefined);
  }, []);

  /** Ctrl+C: interrupt the program (Python raises KeyboardInterrupt, most programs stop). */
  const interrupt = useCallback(() => {
    const l = liveRun.current;
    if (!l?.id) return;
    void sendLiveInput(l.id, { signal: "SIGINT" }).catch(() => undefined);
  }, []);

  /** Pages the preview can go to (web projects): for the address bar's list. */
  const pages = last.current ? [...htmlFiles(last.current.project.files).map((f) => f.path), ...last.current.project.files.filter((f) => /\.php$/i.test(f.path)).map((f) => f.path)] : [];

  return { state, run, runLive, sendLine, sendEof, interrupt, stop, clear, navigate, back, openPage: (path: string) => openPage(path, "GET", "", [], state.page), pages };
}
